/**
 * ContactCard (TASK-238)
 *
 * Card for a single contact (touchpoint): client name, channel badge, funnel
 * stage, outcome, conversation summary and the linked estimate (if any).
 *
 * Extracted from the (removed) ContactsPage so it can be reused on the client
 * detail page Контакты widget.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2, Play, User as UserIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChannelBadge } from '@/components/dashboard/ChannelBadge';
import { useDialogParam } from '@/hooks/useDialogParam';
import { StageBadge, OutcomeBadge } from '@/components/funnel/FunnelBadges';
import { AudioPlayer, formatAudioTime } from '@/components/media/AudioPlayer';
import { funnelApi } from '@/services/api/funnel';
import type { ContactRecordingInfo, FunnelContact } from '@/types/funnel';

// eslint-disable-next-line react-refresh/only-export-components
export function formatContactDate(dateString: string): string {
  return new Date(dateString).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// eslint-disable-next-line react-refresh/only-export-components
export function formatContactCost(value: string | number): string {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(num)) return String(value);
  return `${num.toLocaleString('ru-RU')} ₽`;
}

function contactTitle(contact: FunnelContact): string {
  return contact.client?.name || contact.client?.phone || 'Клиент';
}

/**
 * TASK-329: compact «Запись м:сс» button + dialog with an audio player.
 *
 * The recording endpoint requires auth headers, so the audio is downloaded
 * as a blob through apiClient (only when the dialog is opened) and played
 * from an object URL, which is revoked on close/unmount.
 */
function ContactRecording({
  contactId,
  recording,
}: {
  contactId: string;
  recording: ContactRecordingInfo;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  const {
    data: blob,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['contact-recording', contactId],
    queryFn: () => funnelApi.getContactRecordingBlob(contactId),
    enabled: open,
    staleTime: Infinity,
  });

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [open, blob]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2 h-7 gap-1.5 px-2 text-xs"
        onClick={() => setOpen(true)}
        data-testid="contact-recording-button"
      >
        <Play className="h-3 w-3" />
        <span>
          Запись
          {recording.duration_seconds != null
            ? ` ${formatAudioTime(recording.duration_seconds)}`
            : ''}
        </span>
      </Button>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Запись разговора</DialogTitle>
        </DialogHeader>
        {isLoading && (
          <div
            data-testid="contact-recording-loading"
            className="flex items-center gap-2 py-4 text-sm text-gray-500"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Загрузка записи…
          </div>
        )}
        {isError && (
          <p
            data-testid="contact-recording-error"
            className="py-4 text-sm text-red-600"
          >
            Не удалось загрузить запись. Попробуйте ещё раз.
          </p>
        )}
        {objectUrl && (
          <AudioPlayer
            src={objectUrl}
            durationSeconds={recording.duration_seconds}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ContactCard({ contact }: { contact: FunnelContact }): JSX.Element {
  // KAN-334: транскрипт разговора прямо с карточки клиента (раньше лента
  // реплик была доступна только с дашборда). Реплики клиента доливаются в
  // ленту пост-обработкой (~2 мин после звонка, после расшифровки записи).
  // KAN-710: the modal follows ?dialog=<clientId>&estimate=<id>
  // (TranscriptDialogHost), so the link to it can be shared.
  const { openDialog } = useDialogParam();
  const estimateId = contact.estimate?.id ?? null;
  const openTranscript = () =>
    openDialog({
      clientId: contact.client?.id ?? null,
      estimateId,
      hint: { title: contactTitle(contact) },
    });
  return (
    <Card data-testid={`contact-card-${contact.id}`} className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <UserIcon className="h-4 w-4 shrink-0 text-gray-400" />
              <p className="truncate font-medium text-gray-900">{contactTitle(contact)}</p>
              <ChannelBadge source={contact.channel} />
              {/* KAN-334: это статус КЛИЕНТА СЕЙЧАС, а не итог ЭТОГО звонка
                  (Contact намеренно не носит стадию — её носит клиент). Без
                  подписи одинаковые «Лояльный / Выиграно» на всех 69 касаниях
                  читались как итог каждого разговора и сбивали с толку. */}
              {(contact.client?.stage || contact.client?.outcome) && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                  сейчас:
                  <StageBadge stage={contact.client?.stage} />
                  <OutcomeBadge outcome={contact.client?.outcome} />
                </span>
              )}
            </div>
            {contact.client?.phone && (
              <p className="mt-1 text-xs text-gray-500">{contact.client.phone}</p>
            )}
            {/* KAN-334: сводка звонка выводится ЦЕЛИКОМ. Обрезка в две строки
                прятала ровно то, ради чего оператор открывает карточку («Клиент
                Андрей интересовался подбором оптимальных…»), а развернуть её
                было негде. Переносы из сводки сохраняем. */}
            {contact.summary && (
              <p className="mt-2 whitespace-pre-line text-sm text-gray-600">{contact.summary}</p>
            )}
            {contact.estimate && (
              <div data-testid="contact-estimate" className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-700">
                <FileText className="h-3 w-3 text-gray-400" />
                <span className="truncate">{contact.estimate.display_name}</span>
                <span className="font-medium">{formatContactCost(contact.estimate.total_cost)}</span>
              </div>
            )}
            {(contact.recording || contact.estimate) && (
              <div className="flex flex-wrap items-center gap-2">
                {contact.recording && (
                  <ContactRecording
                    contactId={contact.id}
                    recording={contact.recording}
                  />
                )}
                {contact.estimate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2 h-7 gap-1.5 px-2 text-xs"
                    onClick={openTranscript}
                    data-testid="contact-transcript-button"
                  >
                    <FileText className="h-3 w-3" />
                    <span>Транскрипт</span>
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="shrink-0 text-right">
            <p className="text-xs text-gray-400">{formatContactDate(contact.last_activity_at)}</p>
            {contact.operator?.name && (
              <p className="mt-1 text-xs text-gray-500">Оператор: {contact.operator.name}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default ContactCard;
